import type { AloftPrice, CompType } from '@aloft/toolkit-shared';
import { noop, orUndefined, schAloftPrice } from '@aloft/toolkit-shared';
import { Button, Flex, RadioGroup, Tabs, Typography, theme } from '@aloft/toolkit-sui';

import { useEffect, useMemo, useRef, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { Controller, Form, FormProvider, useWatch } from 'react-hook-form';

import { useTkv2ReferenceBarEvents } from '../../components/ReferenceBar/useTkv2ReferenceBarEvents.ts';
import { ApproachesToValueChart } from '../../components/graphs/reconciliation/ApproachesToValueChart.tsx';
import { useAppraisifyValidation } from '../../hooks/validation/useAppraisifyValidation.ts';
import { submitReconciliation } from '../../redux/features/reconciliationSlice.ts';
import {
  setReferenceViewOpen,
  setSelectedDataSheet,
} from '../../redux/features/referenceBarUISlice.ts';
import { selectCommentary } from '../../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import { selectOrder } from '../../redux/features/selectors/orderSelectors.ts';
import { selectEstimatedROI } from '../../redux/features/selectors/selectEstimatedROI.ts';
import { selectReconciliation } from '../../redux/features/selectors/selectReconciliation.ts';
import { selectAllCompTypesReconciliationData } from '../../redux/features/selectors/selectReconciliationData.ts';
import { useAppDispatch, useAppSelector } from '../../redux/hooks.ts';
import { RequiredFieldAnchor } from '../alerts/RequiredFieldAnchor.tsx';
import { useRequiredFieldFocusEffect } from '../alerts/useRequiredFieldFocusEffect.ts';
import { OpinionsOfValueSection } from './components/OpinionsOfValueSection.tsx';
import { ReconciliationFinalSection } from './components/ReconciliationFinalSection.tsx';
import { ReconciliationRevisionSection } from './components/ReconciliationRevisionSection.tsx';
import { ReconciliationSummarizedValues } from './components/ReconciliationSummarizedValues.tsx';
import { useDesktopReconciliationComparison } from './hooks/useDesktopReconciliationComparison.ts';
import { useReconciliationForm } from './hooks/useReconciliationForm.ts';
import type { ReconciliationFormOutput } from './schemas.ts';
import { getOpinionOfValueOutsideAdjustedSalePriceRangeWarning } from './utils/getOpinionOfValueOutsideAdjustedSalePriceRangeWarning.ts';
import { getMostRecentPreviousCommentaryForComparable } from './utils/getPreviousAlternativeComparablesEntries.ts';
import { getRenovationBudgetBelowValueIncreaseWarning } from './utils/getRenovationBudgetBelowValueIncreaseWarning.ts';

const VALUE_TYPE_ITEMS = [
  {
    label: 'As-is',
    value: 'as-is',
  },
  {
    label:
      'Subject to the following repairs or alterations on the basis of a hypothetical condition that the repairs or alterations have been completed',
    value: 'subject-to',
  },
];

type ReconciliationTab = 'opinions' | 'revision' | 'final';

const findActualScrollRoot = (start: HTMLElement | null): HTMLElement | Window => {
  let el: HTMLElement | null = start;
  while (el) {
    const { overflow, overflowY } = window.getComputedStyle(el);
    const scrollable =
      overflowY === 'auto' ||
      overflowY === 'scroll' ||
      overflow === 'auto' ||
      overflow === 'scroll';

    if (scrollable && el.scrollHeight > el.clientHeight + 1) return el;
    el = el.parentElement;
  }

  return window;
};

const isOutsideIndicatedCostThreshold = (indicated?: AloftPrice, median?: AloftPrice): boolean => {
  if (!indicated || !median || median.unitAmount === 0) return false;
  return Math.abs(indicated.unitAmount - median.unitAmount) / Math.abs(median.unitAmount) > 0.25;
};

export const ReconciliationModule = () => {
  useRequiredFieldFocusEffect();
  const dispatch = useAppDispatch();
  const form = useReconciliationForm();
  const [activeTab, setActiveTab] = useState<ReconciliationTab>('opinions');
  const [opinionViewModes, setOpinionViewModes] = useState<{
    default: 'chart' | 'table';
    aiv: 'chart' | 'table';
    arv: 'chart' | 'table';
  }>({
    default: 'chart',
    aiv: 'chart',
    arv: 'chart',
  });
  const { reconciliation: reconciliationCommentary } = useAppSelector(selectCommentary);
  const reconciliation = useAppSelector(selectReconciliation);
  const order = useAppSelector(selectOrder);
  const {
    isRevision,
    revisionData,
    previousRevisionsData,
    hasArvOption: isArvOrder,
    isMock: isMockOrder,
    includeCostApproach,
  } = order;
  const shouldShowRevisionTab = isRevision || !!revisionData?.revisionType;
  const previousAlternativeComparablesHistories =
    reconciliationCommentary?.previousAlternativeComparablesCommentaries;
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const sectionRefs = useRef<Record<ReconciliationTab, HTMLDivElement | null>>({
    opinions: null,
    revision: null,
    final: null,
  });
  const watchedOpinionOfMarketValue = useWatch({
    control: form.control,
    name: 'opinionOfMarketValue',
  });
  const watchedOpinionOfAfterRepairValue = useWatch({
    control: form.control,
    name: 'opinionOfAfterRepairValue',
  });
  const watchedTotalRenovationBudget = useWatch({
    control: form.control,
    name: 'totalRenovationBudget',
  });
  const watchedArvBelowAivOverride = useWatch({
    control: form.control,
    name: 'arvBelowAivOverride',
  });
  const watchedOpinionOfMarketValuePrice = orUndefined(schAloftPrice.parse)(
    watchedOpinionOfMarketValue,
  );
  const watchedOpinionOfAfterRepairValuePrice = orUndefined(schAloftPrice.parse)(
    watchedOpinionOfAfterRepairValue,
  );
  const watchedTotalRenovationBudgetPrice = orUndefined(schAloftPrice.parse)(
    watchedTotalRenovationBudget,
  );
  const isArvBelowAiv = Boolean(
    isArvOrder &&
      watchedOpinionOfMarketValuePrice &&
      watchedOpinionOfAfterRepairValuePrice &&
      !watchedOpinionOfMarketValuePrice.lt(watchedOpinionOfAfterRepairValuePrice),
  );
  const renovationBudgetWarningMessage = getRenovationBudgetBelowValueIncreaseWarning({
    opinionOfMarketValue: watchedOpinionOfMarketValuePrice,
    opinionOfAfterRepairValue: watchedOpinionOfAfterRepairValuePrice,
    totalRenovationBudget: watchedTotalRenovationBudgetPrice,
  });
  const reconciliationData = useAppSelector(selectAllCompTypesReconciliationData);
  const opinionOfMarketValueRangeWarningMessage =
    getOpinionOfValueOutsideAdjustedSalePriceRangeWarning({
      opinionOfValue: watchedOpinionOfMarketValuePrice,
      minCompMarketAdjustedSalePrice: reconciliationData.general.minCompMarketAdjustedSalePrice,
      maxCompMarketAdjustedSalePrice: reconciliationData.general.maxCompMarketAdjustedSalePrice,
      valueLabel: 'Opinion of Market Value',
    });
  const opinionOfAsIsValueRangeWarningMessage =
    getOpinionOfValueOutsideAdjustedSalePriceRangeWarning({
      opinionOfValue: watchedOpinionOfMarketValuePrice,
      minCompMarketAdjustedSalePrice: reconciliationData.aiv.minCompMarketAdjustedSalePrice,
      maxCompMarketAdjustedSalePrice: reconciliationData.aiv.maxCompMarketAdjustedSalePrice,
      valueLabel: 'Opinion of As-Is Value',
    });
  const opinionOfAfterRepairValueRangeWarningMessage =
    getOpinionOfValueOutsideAdjustedSalePriceRangeWarning({
      opinionOfValue: watchedOpinionOfAfterRepairValuePrice,
      minCompMarketAdjustedSalePrice: reconciliationData.arv.minCompMarketAdjustedSalePrice,
      maxCompMarketAdjustedSalePrice: reconciliationData.arv.maxCompMarketAdjustedSalePrice,
      valueLabel: 'Opinion of After-Repair Value',
    });
  const {
    isHybridOrder: isHybridDesktopComparison,
    priorDesktopAiv,
    priorDesktopArv,
    aivDelta,
    arvDelta,
    hasAivDiscrepancy,
    hasArvDiscrepancy,
  } = useDesktopReconciliationComparison({
    opinionOfMarketValue: watchedOpinionOfMarketValuePrice,
    opinionOfAfterRepairValue: watchedOpinionOfAfterRepairValuePrice,
  });
  const tabs = useMemo(
    () =>
      [
        { label: 'Opinions of Value', value: 'opinions' },
        ...(shouldShowRevisionTab ? [{ label: 'Revision / ROV', value: 'revision' }] : []),
        { label: 'Final Reconciliation', value: 'final' },
      ] as { label: string; value: ReconciliationTab }[],
    [shouldShowRevisionTab],
  );

  useEffect(() => {
    const savedRevisionCommentary = reconciliationCommentary?.revisionCommentary ?? '';
    const previousRevisionCommentary =
      reconciliationCommentary?.previousRevisionCommentaries?.[0] ?? '';
    const savedAlternativeComparablesCommentary =
      reconciliationCommentary?.alternativeComparablesCommentary ?? [];

    form.reset({
      reconciliationCommentary: reconciliationCommentary?.reconciliationCommentary ?? '',
      aivReconciliationCommentary:
        reconciliationCommentary?.aivReconciliationCommentary ??
        reconciliationCommentary?.reconciliationCommentary ??
        '',
      arvReconciliationCommentary: reconciliationCommentary?.arvReconciliationCommentary ?? '',
      highestAndBestUseCommentary: reconciliationCommentary?.highestAndBestUseCommentary ?? '',
      totalCostToCure: orUndefined(schAloftPrice.parse)(reconciliation.totalCostToCure),
      totalCostToCureCommentary: reconciliationCommentary?.totalCostToCureCommentary ?? '',
      opinionOfMarketValue: orUndefined(schAloftPrice.parse)(reconciliation.opinionOfMarketValue),
      opinionOfAfterRepairValue: orUndefined(schAloftPrice.parse)(
        reconciliation.opinionOfAfterRepairValue,
      ),
      totalRenovationBudget: orUndefined(schAloftPrice.parse)(reconciliation.totalRenovationBudget),
      revisionCommentary: savedRevisionCommentary.trim()
        ? savedRevisionCommentary
        : previousRevisionCommentary,
      alternativeComparablesCommentary:
        revisionData?.comparables?.map((comp, index) => {
          const saved = savedAlternativeComparablesCommentary[index] ?? '';
          if (saved.trim()) return saved;
          return getMostRecentPreviousCommentaryForComparable(
            previousAlternativeComparablesHistories,
            comp.address,
            index,
          );
        }) ?? savedAlternativeComparablesCommentary,
      feasibilityCommentary: reconciliationCommentary?.feasibilityCommentary ?? '',
      aivDiscrepancyCommentary: reconciliationCommentary?.aivDiscrepancyCommentary ?? '',
      arvDiscrepancyCommentary: reconciliationCommentary?.arvDiscrepancyCommentary ?? '',
      arvBelowAivOverrideCommentary: reconciliationCommentary?.arvBelowAivOverrideCommentary ?? '',
      hasAivDiscrepancy,
      hasArvDiscrepancy,
      valueTypes: reconciliation.valueTypes,
      arvBelowAivOverride: reconciliation.arvBelowAivOverride ?? false,
    });
  }, [
    reconciliationCommentary,
    reconciliation,
    hasAivDiscrepancy,
    hasArvDiscrepancy,
    revisionData?.comparables,
    previousAlternativeComparablesHistories,
  ]);

  useEffect(() => {
    form.setValue('hasAivDiscrepancy', hasAivDiscrepancy);
    form.setValue('hasArvDiscrepancy', hasArvDiscrepancy);
  }, [form, hasAivDiscrepancy, hasArvDiscrepancy]);

  const validation = useAppraisifyValidation();
  const {
    totalCostToCure: totalCostToCureError,
    totalCostToCureCommentary: totalCostToCureCommentaryError,
    opinionOfMarketValue: opinionOfMarketValueError,
    opinionOfAfterRepairValue: opinionOfAfterRepairValueError,
    totalRenovationBudget: totalRenovationBudgetError,
    highestAndBestUseCommentary: highestAndBestUseCommentaryError,
    reconciliationCommentary: reconciliationCommentaryError,
    aivReconciliationCommentary: aivReconciliationCommentaryError,
    arvReconciliationCommentary: arvReconciliationCommentaryError,
    revisionCommentary: revisionCommentaryError,
    alternativeComparablesCommentary: alternativeComparablesCommentaryError,
    feasibilityCommentary: feasibilityCommentaryError,
    aivDiscrepancyCommentary: aivDiscrepancyCommentaryError,
    arvDiscrepancyCommentary: arvDiscrepancyCommentaryError,
    arvBelowAivOverrideCommentary: arvBelowAivOverrideCommentaryError,
  } = validation.errors.Reconciliation;

  const showIndicatedCostWarningGeneral = useMemo(
    () =>
      includeCostApproach &&
      !isArvOrder &&
      isOutsideIndicatedCostThreshold(
        reconciliationData.general.costApproachValue,
        reconciliationData.general.medianCompMarketAdjustedSalePrice,
      ),
    [isArvOrder, reconciliationData, includeCostApproach],
  );
  const showIndicatedCostWarningAiv = useMemo(
    () =>
      includeCostApproach &&
      isArvOrder &&
      isOutsideIndicatedCostThreshold(
        reconciliationData.aiv.costApproachValue,
        reconciliationData.aiv.medianCompMarketAdjustedSalePrice,
      ),
    [isArvOrder, reconciliationData, includeCostApproach],
  );
  const showIndicatedCostWarningArv = useMemo(
    () =>
      includeCostApproach &&
      isArvOrder &&
      isOutsideIndicatedCostThreshold(
        reconciliationData.arv.costApproachValue,
        reconciliationData.arv.medianCompMarketAdjustedSalePrice,
      ),
    [isArvOrder, reconciliationData, includeCostApproach],
  );

  const estimatedROI = isArvOrder ? useAppSelector(selectEstimatedROI) : undefined;

  const shouldShowFeasibilityCommentary = useMemo(() => {
    if (!estimatedROI) return false;
    return estimatedROI.toDollarLike() <= 0;
  }, [estimatedROI]);

  useEffect(() => {
    dispatch(setSelectedDataSheet('pdf'));
    dispatch(setReferenceViewOpen(true));
  }, []);

  const onSubmit: SubmitHandler<ReconciliationFormOutput> = (data) => {
    if (isArvOrder) {
      dispatch(
        submitReconciliation({
          ...data,
          reconciliationCommentary: data.aivReconciliationCommentary,
        }),
      );
    } else {
      dispatch(
        submitReconciliation({
          ...data,
          aivReconciliationCommentary: undefined,
          arvReconciliationCommentary: undefined,
        }),
      );
    }
  };

  const handleChange = () => {
    form.handleSubmit(onSubmit, undefined)().catch(noop);
  };

  const valueTypesSubHeader = (
    <Flex
      borderColor="strokeDefault"
      borderStyle="solid"
      borderWidth="1px"
      borderRadius="5px"
      padding="15px"
      flexDirection="column"
      marginBottom="15px"
      gap={4}
      backgroundColor="surface1"
    >
      <Typography variant="body1" color="textSecondary">
        This appraisal is made:
      </Typography>
      <Controller
        name="valueTypes"
        control={form.control}
        render={({ field }) => (
          <RequiredFieldAnchor
            fieldName="valueTypes"
            isFieldComplete={Boolean(field.value?.length)}
          >
            <RadioGroup
              name="valueType"
              items={VALUE_TYPE_ITEMS}
              onChange={(value) => {
                field.onChange([value]);
                handleChange();
              }}
              value={field.value?.[0] ?? ''}
              size="md"
              textVariant="h5"
              gap="8px"
            />
          </RequiredFieldAnchor>
        )}
      />
    </Flex>
  );

  let roiDisplay = 'TBD';
  if (estimatedROI) {
    roiDisplay =
      estimatedROI.toDollarLike() < 0
        ? `-$${Math.abs(estimatedROI.toDollarLike()).toLocaleString()}`
        : estimatedROI.toString(true);
  }

  let roiDisplayColor = theme.colors.hoverTransparent;
  if (estimatedROI) {
    roiDisplayColor =
      estimatedROI.toDollarLike() < 0 ? theme.colors.textError : theme.colors.textSuccess;
  }

  const renderChartOrSummary = (compType?: CompType) => {
    const modeKey = compType ?? 'default';
    const opinionViewMode = opinionViewModes[modeKey];
    const setOpinionViewMode = (mode: 'chart' | 'table') =>
      setOpinionViewModes((prev) => ({ ...prev, [modeKey]: mode }));
    const switcher = (
      <Flex
        border="2px solid surfaceAccent7"
        borderRadius="8px"
        overflow="hidden"
        width="fit-content"
        backgroundColor="surface1"
      >
        <Button
          type="button"
          variant="tertiary"
          text="Chart"
          size="sm"
          onClick={() => setOpinionViewMode('chart')}
          __dangerDoNotUseSx={{
            borderRadius: 0,
            borderRight: `1px solid ${theme.colors.surfaceAccent1}`,
            backgroundColor:
              opinionViewMode === 'chart' ? theme.colors.surfaceAccent5 : theme.colors.surface1,
            color: theme.colors.surfaceAccent1,
            fontWeight: 700,
            fontSize: '14px',
            lineHeight: '20px',
            padding: '8px 20px',
          }}
        />
        <Button
          type="button"
          variant="tertiary"
          text="Table"
          size="sm"
          onClick={() => setOpinionViewMode('table')}
          __dangerDoNotUseSx={{
            borderRadius: 0,
            backgroundColor:
              opinionViewMode === 'table' ? theme.colors.surfaceAccent5 : theme.colors.surface1,
            color: theme.colors.surfaceAccent1,
            fontWeight: 700,
            fontSize: '14px',
            lineHeight: '24px',
            padding: '4px 10px',
          }}
        />
      </Flex>
    );

    if (opinionViewMode === 'chart') {
      return <ApproachesToValueChart compType={compType} inlineSwitcher={switcher} />;
    }
    return <ReconciliationSummarizedValues compType={compType} inlineSwitcher={switcher} />;
  };

  useEffect(() => {
    const inner = scrollContainerRef.current;
    if (!inner) return;

    const scrollRoot = findActualScrollRoot(inner);

    const handleScroll = () => {
      const containerRect =
        scrollRoot instanceof Window
          ? new DOMRect(0, 0, window.innerWidth, window.innerHeight)
          : scrollRoot.getBoundingClientRect();
      const clientHeight =
        scrollRoot instanceof Window ? window.innerHeight : scrollRoot.clientHeight;
      const scrollHeight =
        scrollRoot instanceof Window
          ? document.documentElement.scrollHeight
          : scrollRoot.scrollHeight;
      const scrollTop = scrollRoot instanceof Window ? window.scrollY : scrollRoot.scrollTop;
      const isAtBottom = scrollHeight - scrollTop - clientHeight <= 1;

      if (isAtBottom) {
        const lastTab = tabs.at(-1);
        if (lastTab) setActiveTab(lastTab.value);
        return;
      }

      const entries = tabs
        .map(({ value }) => ({
          tab: value,
          el: sectionRefs.current[value],
        }))
        .filter((entry): entry is { tab: ReconciliationTab; el: HTMLDivElement } => !!entry.el)
        .map(({ tab, el }) => ({
          tab,
          rect: el.getBoundingClientRect(),
        }))
        .filter(({ rect }) => rect.top + rect.height / 4 >= containerRect.top)
        .map(({ tab, rect }) => ({
          tab,
          distance: Math.abs(rect.top - containerRect.top),
        }))
        .sort((a, b) => a.distance - b.distance);
      const nextActive = entries[0];
      if (nextActive) {
        setActiveTab(nextActive.tab);
      }
    };

    scrollRoot.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollRoot.removeEventListener('scroll', handleScroll);
  }, [tabs]);

  const handleTabOnClick = (tab: ReconciliationTab) => {
    setActiveTab(tab);
    sectionRefs.current[tab]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Flex {...useTkv2ReferenceBarEvents()} width="100%">
      <FormProvider {...form}>
        <Form style={{ width: '100%' }} onChange={handleChange}>
          <Flex
            margin="15px"
            flexDirection="column"
            gap={4}
            flex={1}
            width="95%"
            minWidth="600px"
            maxWidth="900px"
            paddingBottom="50px"
          >
            <Flex
              position="sticky"
              top={0}
              zIndex={3}
              backgroundColor="surface1"
              paddingTop="4px"
              paddingBottom="4px"
              flexDirection="column"
              gap={2}
            >
              <Typography variant="h1">Reconciliation</Typography>
              <Tabs tabs={tabs} activeTab={activeTab} setActiveTab={handleTabOnClick} />
            </Flex>
            <Flex
              ref={scrollContainerRef}
              flexDirection="column"
              gap={4}
              overflow="auto"
              minHeight={0}
            >
              <OpinionsOfValueSection
                active={activeTab === 'opinions'}
                form={form}
                isArvOrder={isArvOrder}
                isMockOrder={isMockOrder}
                includeTotalCostToCure={reconciliation?.includeTotalCostToCure}
                priorDesktopAiv={priorDesktopAiv}
                priorDesktopArv={priorDesktopArv}
                watchedOpinionOfMarketValuePrice={watchedOpinionOfMarketValuePrice}
                watchedOpinionOfAfterRepairValuePrice={watchedOpinionOfAfterRepairValuePrice}
                watchedTotalRenovationBudgetPrice={watchedTotalRenovationBudgetPrice}
                aivDelta={aivDelta}
                arvDelta={arvDelta}
                hasAivDiscrepancy={hasAivDiscrepancy}
                hasArvDiscrepancy={hasArvDiscrepancy}
                isHybridDesktopComparison={isHybridDesktopComparison}
                renovationBudgetWarningMessage={renovationBudgetWarningMessage}
                opinionOfMarketValueRangeWarningMessage={opinionOfMarketValueRangeWarningMessage}
                opinionOfAsIsValueRangeWarningMessage={opinionOfAsIsValueRangeWarningMessage}
                opinionOfAfterRepairValueRangeWarningMessage={
                  opinionOfAfterRepairValueRangeWarningMessage
                }
                showIndicatedCostWarningGeneral={showIndicatedCostWarningGeneral}
                showIndicatedCostWarningAiv={showIndicatedCostWarningAiv}
                showIndicatedCostWarningArv={showIndicatedCostWarningArv}
                shouldShowFeasibilityCommentary={shouldShowFeasibilityCommentary}
                roiDisplay={roiDisplay}
                roiDisplayColor={roiDisplayColor}
                totalCostToCureError={totalCostToCureError}
                totalCostToCureCommentaryError={totalCostToCureCommentaryError}
                opinionOfMarketValueError={opinionOfMarketValueError}
                opinionOfAfterRepairValueError={opinionOfAfterRepairValueError}
                totalRenovationBudgetError={totalRenovationBudgetError}
                feasibilityCommentaryError={feasibilityCommentaryError}
                aivDiscrepancyCommentaryError={aivDiscrepancyCommentaryError}
                arvDiscrepancyCommentaryError={arvDiscrepancyCommentaryError}
                isArvBelowAiv={isArvBelowAiv}
                arvBelowAivOverride={watchedArvBelowAivOverride}
                arvBelowAivOverrideCommentaryError={arvBelowAivOverrideCommentaryError}
                renderChartOrSummary={renderChartOrSummary}
                handleChange={handleChange}
                setSectionRef={(el) => {
                  sectionRefs.current.opinions = el;
                }}
              />
              {shouldShowRevisionTab && (
                <ReconciliationRevisionSection
                  active={activeTab === 'revision'}
                  form={form}
                  isMockOrder={isMockOrder}
                  reconciliationCommentary={reconciliationCommentary}
                  revisionData={revisionData}
                  previousRevisionsData={previousRevisionsData}
                  revisionCommentaryError={revisionCommentaryError}
                  alternativeComparablesCommentaryError={alternativeComparablesCommentaryError}
                  setSectionRef={(el) => {
                    sectionRefs.current.revision = el;
                  }}
                />
              )}
              <ReconciliationFinalSection
                active={activeTab === 'final'}
                form={form}
                isArvOrder={isArvOrder}
                isMockOrder={isMockOrder}
                valueTypesSubHeader={valueTypesSubHeader}
                reconciliationCommentaryError={reconciliationCommentaryError}
                aivReconciliationCommentaryError={aivReconciliationCommentaryError}
                arvReconciliationCommentaryError={arvReconciliationCommentaryError}
                highestAndBestUseCommentaryError={highestAndBestUseCommentaryError}
                setSectionRef={(el) => {
                  sectionRefs.current.final = el;
                }}
              />
            </Flex>
          </Flex>
        </Form>
      </FormProvider>
    </Flex>
  );
};
